import {
    ArrowUpTrayIcon,
    BookOpenIcon,
    ChatBubbleLeftIcon,
    ChatBubbleLeftRightIcon,
    HomeIcon,
    Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import { isEmpty } from 'radash';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { ReactComponent as Clockcounterwise } from '@/assets/icons/counterclockwise.svg';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogTabsList,
    DialogTabsTrigger,
} from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { Loader } from '@/components/core/Loader';
import { Tabs, TabsContent } from '@/components/core/Tabs';
import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { DiscardChangesConfirmationModal } from '@/components/DiscardChangesConfirmationModal';
import { FormResetProvider, useFormReset } from '@/dialogs/item-editor/Context/FormResetContext';
import { useItemReloadFlow } from '@/dialogs/item-editor/hooks/useItemReloadFlow';
import { usePersistedItem } from '@/dialogs/item-editor/hooks/usePersistedItem';
import { useDataModel } from '@/hooks/useDataModel';
import { clearExcludedFromSaveFields } from '@/hooks/useExcludedFromSaveFields';
import { clearGlobalReversedMultiReferenceState } from '@/hooks/useReversedMultiReferenceState';
import { useTranslation } from '@/hooks/useTranslation';
import ItemsPaginationFooter from '@/pages/item-details/ItemsPaginationFooter';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { PersistedItem } from '@/types/DataModel';

import { LayoutProvider } from '../../components/datamodel/hooks/useLayout';
import { ItemEditorForm } from './components/ItemEditorForm';
import { ReloadItemDialog } from './components/ReloadItemDialog';
import { FocusAttributeContext } from './Context/FocusAttributeContext';
import { useHasUnsavedItemChanges } from './hooks/useDirtyFields';
import { useItemEditedChannel } from './hooks/useItemEditedChannel';
import { useRealTimeItemChangesListener } from './hooks/useRealTimeItemChangesListener';
import { ConnectedUsers } from './item-editor-footer/ConnectedUsers';
import { EditorDialogButtons } from './item-editor-footer/EditorDialogButtons';
import { ItemEditorDialogHeader } from './item-editor-header/ItemEditorDialogHeader';
import { ItemOrigin } from './ItemEditor.types';
import { resolveStickyEditorTab } from './resolveStickyEditorTab';
import { ItemDetailsTab } from './tabs/ItemDetailsTab/ItemDetailsTab';
import { ItemEditorTab } from './tabs/ItemEditorTab/ItemEditorTab';
import { ItemGroupingTab } from './tabs/ItemGroupingTab/ItemGroupingTab';
import { ItemHierarchiesTabs } from './tabs/ItemHierarchiesTabs/ItemHierarchiesTabs';
import { ItemHierarchiesTabTriggers } from './tabs/ItemHierarchiesTabs/ItemHierarchiesTabTriggers';
import { ItemHistoryTab } from './tabs/ItemHistoryTab/ItemHistoryTab';
import { ItemPublicationsStatus } from './tabs/ItemPublicationStatus/ItemPublicationsStatus';
import { ItemReviews } from './tabs/ItemReviewTab/ItemReviews';
import { ItemReviewTab } from './tabs/ItemReviewTab/ItemReviewTab';

const editorTabPanelClassName =
    'focus-visible:!tw-ring-0 focus-visible:!tw-ring-offset-0 focus-visible:!tw-outline-none';

// clears the open item before the next one is loaded.
const clearedValueForItemNavigation = (value: unknown): unknown => {
    if (Array.isArray(value)) return [];
    if (value !== null && typeof value === 'object' && !(value instanceof Date)) return {};
    return '';
};

interface ItemEditorDialogProps {
    category?: string;
    editorState: any;
    forceCloseTask: any;
    onClose: (item?: PersistedItem) => void;
    openItemEditor: (item?: PersistedItem) => void;
    originalPrimaryKey: string;
    primaryKey: string;
    task: string;
    items: any;
    templateForItemCopy?: object;
    templateForPreparedDefaultItem?: object;
    // Explicit flag set by items-controller when the editor is opened via
    // the "Prepared Default Item" flow. Origin must not be inferred from
    // the emptiness of templateForPreparedDefaultItem — see LAX-10101.
    fromPreparedDefaultItem?: boolean;
}

// Component to handle navigation with form reset
const ItemEditorDialogContent = ({
    category,
    editorState,
    forceCloseTask,
    onClose,
    openItemEditor,
    originalPrimaryKey,
    primaryKey,
    task,
    items,
    templateForItemCopy,
    templateForPreparedDefaultItem,
    fromPreparedDefaultItem,
}: ItemEditorDialogProps) => {
    const { reset, getValues } = useFormContext();
    const { setIsResetting, resetAllMultiReferences } = useFormReset();

    const { clearState } = useAttributesStateStore(useShallow(state => ({
        clearState: state.clearState,
    })));

    const handleNavigateToItem = useCallback((_primaryKey: string) => {
        // Reset form state when navigating to a new item
        setIsResetting(true);

        // FIXME: why not drop all the values?, "reset({})"
        // Clear current form values. Arrays stay arrays so useFieldArray resets,
        // and plain objects stay objects so dimensional values (price, language)
        // are not turned into '' before the next item is hydrated.
        const currentValues = getValues();
        const resetValues = Object.fromEntries(
            Object.keys(currentValues).map((key) => [key, clearedValueForItemNavigation(currentValues[key])])
        );

        reset(resetValues, { keepDirty: false });

        // Reset attribute states
        clearState();

        // Clear reversed multi-reference state to prevent save button from staying active
        clearGlobalReversedMultiReferenceState();

        // Clear excluded from save fields when navigating to a new item
        clearExcludedFromSaveFields();

        // Reset multi-reference
        resetAllMultiReferences();

        // Reset the resetting state after a brief delay
        setTimeout(() => setIsResetting(false), 0);
    }, [reset, getValues, setIsResetting, clearState, resetAllMultiReferences]);

    return (
        <ItemEditorDialogInner
            category={category}
            editorState={editorState}
            forceCloseTask={forceCloseTask}
            onClose={onClose}
            originalPrimaryKey={originalPrimaryKey}
            primaryKey={primaryKey}
            task={task}
            items={items}
            templateForItemCopy={templateForItemCopy}
            templateForPreparedDefaultItem={templateForPreparedDefaultItem}
            fromPreparedDefaultItem={fromPreparedDefaultItem}
            onNavigateToItem={handleNavigateToItem}
            openItemEditor= {openItemEditor}
        />
    );
};

interface ItemEditorDialogInnerProps extends ItemEditorDialogProps {
    onNavigateToItem: (primaryKey: string) => void;
}

const ItemEditorDialogInner = ({
    category,
    editorState,
    forceCloseTask,
    onClose,
    openItemEditor,
    originalPrimaryKey,
    primaryKey,
    task,
    items,
    templateForItemCopy,
    templateForPreparedDefaultItem,
    fromPreparedDefaultItem,
    onNavigateToItem,
}: ItemEditorDialogInnerProps) => {

    const CUSTOM_ITEM_REVIEW_SERVICE = 'CustomItemReviewService';

    const [openReloadEditorDialog, setOpenReloadEditorDialog] = useState(false);
    const [reloadItemMessage, setReloadItemMessage] = useState('');

    const $angularJSInjector = getInjector();
    const $rootScope = getRootScope($angularJSInjector);
    const $location = $angularJSInjector.get('$location');
    const { reset } = useFormContext();

    const [selectedPrimaryKey, setSelectedPrimaryKey] = useState(primaryKey);
    const [originalPrimaryKey__, setOriginalPrimaryKey__] = useState<string | null>(originalPrimaryKey);
    const [savedItemPrimaryKey, setSavedItemPrimaryKey] = useState<string | null>(null);

    const { clearState} = useAttributesStateStore(useShallow(state => ({
        clearState: state.clearState,
    })));
    const [discardChangesConfirmationModal, setDiscardChangesConfirmationModal] = useState(false);
    const [pendingTab, setPendingTab] = useState<string | null>(null);

    const onShowItemDetails = $rootScope.onShowItemDetails;
    const [open, setOpen] = useState(true);
    const [currentTab, setCurrentTab] = useState(!onShowItemDetails ? 'item-editor' : 'item-details');
    const { t } = useTranslation();
    const [onShowItemReview, setOnShowItemReview] = useState(false);
    const [showReceivedReviews, setShowReceivedReviews] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [reviewSectionDirty, setReviewSectionDirty] = useState(false);
    const [showReviewConfirmationModal, setShowReviewConfirmationModal] = useState(false);
    const focusContext = useContext(FocusAttributeContext);
    if (!focusContext) {
        throw new Error('ItemEditorDialog must be used within a FocusAttributeProvider');
    }
    const { setFocusAttribute, setPinnedAttributeName } = focusContext;
    const storedOnlineitemsState = sessionStorage.getItem('items');
    const onlineItemsState = !isEmpty(items) ? items : (storedOnlineitemsState && JSON.parse(storedOnlineitemsState));

    // Used to keep track of open sections for ...
    // - Tab Changes for the same item
    // - navigate to different item from using hierarchy items dropdown menu
    // - navigate to different item using the footer pagination (next/previous)
    const [openEditorSections, setOpenEditorSections] = useState<string[]>([]);
    // One-shot: after the editor is already open, the next item change should
    // bring the sticky open section into the editor viewport.
    const [scrollStickySection, setScrollStickySection] = useState(false);
    const skipStickySectionScroll = useRef(true);

    const [currentItemIndex, setCurrentItemIndex] = useState<number>(0);

    const { itemOrigin, persistedItem, isLoadingPersistedItem, refresh: refreshPersistedItem } =
        usePersistedItem({
            category: category,
            primaryKey: savedItemPrimaryKey || selectedPrimaryKey,
            originalPrimaryKey: originalPrimaryKey,
            templateForItemCopy: templateForItemCopy,
            templateForPreparedDefaultItem: templateForPreparedDefaultItem,
            fromPreparedDefaultItem: fromPreparedDefaultItem,
        });
    // A prepared-default create already has an item payload, but History,
    // grouping, details, hierarchies, reviews, and publication status belong
    // to a saved item. They stay hidden until that item has a primary key.
    const isSavedItem = !isEmpty(persistedItem?.primaryKey__);
    const hasUnsavedChanges = useHasUnsavedItemChanges(persistedItem);

    const { dataModel } = useDataModel();
    const itemHierarchies = persistedItem?.category__
        ? dataModel?.category(persistedItem.category__)?.hierarchies ?? []
        : [];
    const resolvedTab = persistedItem
        ? resolveStickyEditorTab(currentTab, persistedItem, itemHierarchies)
        : currentTab;

    useEffect(() => {
        if (resolvedTab !== currentTab) {
            setCurrentTab(resolvedTab);
        }
    }, [resolvedTab, currentTab]);

    useEffect(() => {
        const getCurrentItemIndex = (item: any) => {
            if (Array.isArray(onlineItemsState) && onlineItemsState.length > 0) {
                const index = onlineItemsState.reduce(
                    (acc: number | null, currentValue: PersistedItem, index: number) => {
                        if (
                            currentValue?.primaryKey__ === item?.primaryKey__ &&
                            acc === null
                        ) {
                            return index;
                        }
                        return acc;
                    },
                    null,
                );
                setCurrentItemIndex(index);
            }
        };
        if (persistedItem) {
            getCurrentItemIndex(persistedItem);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingPersistedItem, persistedItem?.primaryKey__]);

    // Reset savedItemPrimaryKey when navigating to a different item
    useEffect(() => {
        setSavedItemPrimaryKey(null);
    }, [selectedPrimaryKey]);

    useEffect(() => {
        if (skipStickySectionScroll.current) {
            skipStickySectionScroll.current = false;
            return;
        }
        setScrollStickySection(true);
    }, [selectedPrimaryKey]);

    const handleStickySectionScrolled = useCallback(() => {
        setScrollStickySection(false);
    }, []);

    const handleItemSaved = (savedItem: PersistedItem) => {
        setSavedItemPrimaryKey(savedItem.primaryKey__);
        setOriginalPrimaryKey__(null);
    };
    const closeItemEditor = (item?: PersistedItem, options?: { clearDraft?: boolean }) => {
        // Default to clearing the draft, so user-initiated closes (X / Cancel /
        // Discard / Save) wipe any leftover `tmpItem:*` entry. The reload-confirm
        // flow opts out via `clearDraft: false` so the draft survives the
        // intermediate close+reopen and can be restored on the new mount.
        const { clearDraft = true } = options ?? {};
        if (clearDraft) {
            clearTempItem();
        }
        setPinnedAttributeName(undefined);
        $rootScope.onShowItemDetails = false;
        $rootScope.$broadcast('itemEditorClosed');
        clearState();
        setSavedItemPrimaryKey(null);
        onClose(item);
        setSavedItemPrimaryKey(null);
    };

    const OnCloseItemReview = ()=> {
        if (reviewSectionDirty) {
            setShowReviewConfirmationModal(true);
        } else {
            //Discard Item Review
            setOnShowItemReview(false);
            //Hide Received reviews
            setShowReceivedReviews(false);
        }
    };

    const handleReviewSectionChange = (isDirty: boolean) => {
        setReviewSectionDirty(isDirty);
    };

    const handleReviewNavigation = () => {
        if (onShowItemReview && reviewSectionDirty) {
            // If we're currently showing review and it's dirty, show confirmation modal
            setShowReviewConfirmationModal(true);
        } else {
            // Toggle the review state
            setOnShowItemReview(!onShowItemReview);
            setShowReceivedReviews(false);
        }
    };

    const handleReviewConfirmationDiscard = () => {
        setReviewSectionDirty(false);
        setShowReviewConfirmationModal(false);
        setOnShowItemReview(false);
        setShowReceivedReviews(false);
        if (pendingTab) {
            // Refresh ItemEditorTab when switching back to it from another tab
            if (pendingTab === 'item-editor' && currentTab !== 'item-editor') {
                // Refetch the item data
                refreshPersistedItem();
            }
            setCurrentTab(pendingTab);
            setPendingTab(null);
        }
    };

    const handleReviewConfirmationClose = () => {
        setShowReviewConfirmationModal(false);
        setShowConfirmationModal(false);
    };

    const handleDiscardChanges = () => {
        $rootScope.isDirty = false;
        setDiscardChangesConfirmationModal(false);
        if (pendingTab) {
            // Refresh ItemEditorTab when switching back to it from another tab
            if (pendingTab === 'item-editor' && currentTab !== 'item-editor') {
                // Refetch the item data
                refreshPersistedItem();
            }
            setCurrentTab(pendingTab);
            setPendingTab(null);
            reset(persistedItem, { keepDirty: false });
        } else {
            closeItemEditor(persistedItem);
        }
    };

    const handleCloseDiscardModal = () => {
        if (showConfirmationModal) {
            setShowConfirmationModal(false);
        }
        setDiscardChangesConfirmationModal(false);
    };

    const handleEscapeDiscardChanges = () => {
        $rootScope.isDirty = false;
        closeItemEditor(persistedItem);
    };

    const enabledItemReview = () => {
        const customService =  $rootScope.getService(CUSTOM_ITEM_REVIEW_SERVICE);
        const item = {...persistedItem, category__: category};
        const isEnabledByDataModel = !customService || customService.enableItemReview(item, $rootScope.user, $rootScope.organization);
        return (!isEmpty(persistedItem?.channel__) || !isEmpty(persistedItem?.supplier__)) && isEnabledByDataModel;
    };

    const unsetFocusAttribute = useCallback(() => {
        const currentPath = $location.path();
        const newUrl = currentPath.split('/').slice(0, 3).join('/');
        setFocusAttribute(undefined);
        $location.displayUrl(newUrl);
    }, [setFocusAttribute, $location]);

    // Centralized reload flow
    const refreshItem = ()=>{
        // Keep the just-persisted draft so it can be restored on the new mount.
        closeItemEditor(persistedItem, { clearDraft: false });
        openItemEditor(persistedItem);
    };

    const { openReloadEditorDialog: openReload, reloadItemMessage: reloadMsg, handleConfirmReload, clearTempItem } = useItemReloadFlow({
        selectedPrimaryKey: selectedPrimaryKey,
        refreshItem,
        persistedItem,
        isLoadingPersistedItem,
    });
    useEffect(() => setOpenReloadEditorDialog(openReload), [openReload]);
    useEffect(() => setReloadItemMessage(reloadMsg), [reloadMsg]);

    // Single owner for ITEM_EDITED channel register/unregister (listen=true so
    // ConnectedUsers receives join/leave). Field sync + ConnectedUsers must not
    // register again — that was causing 4× itemEdited?identifier=… on open.
    const itemEditedChannelPrimaryKey =
        persistedItem?.primaryKey__ || selectedPrimaryKey || undefined;
    useItemEditedChannel({
        primaryKey: itemEditedChannelPrimaryKey,
        listen: true,
    });
    useRealTimeItemChangesListener({
        primaryKey: persistedItem?.primaryKey__,
    });
    return (
        <Dialog open={open} onOpenChange={open => { setOpen(open); closeItemEditor(); }}>
            <DialogContent
                variant="screen"
                onPointerDownOutside={e => e.preventDefault()}
                onInteractOutside={e => e.preventDefault()}
                onOpenAutoFocus={e => e.preventDefault()}
                onDiscardChanges={handleEscapeDiscardChanges}
                isDirty={open && hasUnsavedChanges}
                className='modal-dialog tw-m-0'>
                <LayoutProvider layoutName='edit'>
                    <DiscardChangesConfirmationModal
                        show={showConfirmationModal || discardChangesConfirmationModal}
                        onDiscard={handleDiscardChanges}
                        onClose={handleCloseDiscardModal}
                    />
                    {itemOrigin !== ItemOrigin.NEW
                        && itemOrigin !== ItemOrigin.PREPARED_DEFAULT
                        && isLoadingPersistedItem ? (
                            <div className="tw-flex tw-items-center tw-justify-center tw-h-[80vh] tw-w-full">
                                <Loader />
                            </div>
                        ) : (
                            <>
                                <ItemEditorDialogHeader
                                    category={category}
                                    isCreatingNewItem={isEmpty(savedItemPrimaryKey || selectedPrimaryKey)}
                                    persistedItem={persistedItem}
                                    onNavigateToItem={(primaryKey: string) => {
                                        unsetFocusAttribute();
                                        onNavigateToItem(primaryKey);
                                        setSelectedPrimaryKey(primaryKey);
                                        setOnShowItemReview(false);
                                        setShowReceivedReviews(false);
                                    }}
                                />
                                <Tabs
                                    className="tw-flex tw-flex-col tw-px-5 lg:tw-px-8 tw-py-3 tw-overflow-auto tw-h-full"
                                    defaultValue={!onShowItemDetails ? 'item-editor': 'item-details'}
                                    value={resolvedTab}
                                    onValueChange={(nextTab) => {
                                        if (hasUnsavedChanges && currentTab === 'item-editor' && nextTab !== 'item-editor') {
                                        // Block tab switch and show discard changes modal
                                            setPendingTab(nextTab);
                                            setDiscardChangesConfirmationModal(true);
                                        } else if(reviewSectionDirty) {
                                            setPendingTab(nextTab);
                                            setShowReviewConfirmationModal(true);
                                        } else {
                                        // Refresh ItemEditorTab when switching back to it from another tab
                                            if (nextTab === 'item-editor' && currentTab !== 'item-editor') {
                                            // Refetch the item data
                                                refreshPersistedItem();
                                            }
                                            setCurrentTab(nextTab);
                                        }
                                    }}
                                    id='item-editor-body'
                                >
                                    <DialogTabsList className="tw-z-50
                                    tw-top-[-56px] tw-left-5 tw-flex-row
                                    tw-rounded-tl-lg tw-rounded-tr-lg tw-rounded-bl-none tw-rounded-br-none
                                    [&>*:last-child]:tw-rounded-tr-lg
                                    md:tw-left-10
                                    lg:tw-top-4 lg:tw-left-[-56px] lg:tw-flex-col
                                    lg:tw-rounded-tr-none lg:tw-rounded-bl-lg
                                    lg:[&>*:last-child]:tw-rounded-tr-none">
                                        <DialogTabsTrigger value="item-editor"  onClick={()=> OnCloseItemReview()} title={t('ITEM.HOUSE')}
                                            className={'tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-w-[56px] tw-h-[56px]'}>
                                            <Icon icon={HomeIcon} size="lg" variant="dark" className='tw-object-contain !tw-mt-[-2px]' />
                                        </DialogTabsTrigger>

                                        {
                                            (isSavedItem && enabledItemReview()) &&
                                            <DialogTabsTrigger
                                                id="editor-review-tab"
                                                value={currentTab}
                                                onClick={handleReviewNavigation}
                                                className={`tw-flex tw-items-center tw-justify-center tw-w-[56px] tw-h-[56px]
                                                ${!onShowItemReview ? '!tw-bg-transparent' : ''}`}
                                                title={!onShowItemReview ? t('ITEM.CREATE_REVIEW') : t('MODAL.DISCARD_ITEM_REVIEW')}>
                                                <Icon icon={ChatBubbleLeftIcon} size="lg" variant="dark" className='tw-object-contain !tw-mt-[-2px]'/>
                                            </DialogTabsTrigger>
                                        }
                                        {
                                            (isSavedItem && persistedItem && !(itemOrigin === ItemOrigin.NEW) && !isEmpty(persistedItem.reviewCountTotal__)) &&
                                            <DialogTabsTrigger
                                                value={currentTab}
                                                onClick={() => {
                                                    !reviewSectionDirty && setShowReceivedReviews(!showReceivedReviews);
                                                    reviewSectionDirty ? handleReviewNavigation () : setOnShowItemReview(false);
                                                }}
                                                className={`tw-flex tw-items-center tw-justify-center tw-w-[56px] tw-h-[56px] ${!showReceivedReviews ? '!tw-bg-transparent' : ''}`}
                                                title={ !showReceivedReviews ? t('ITEM.SHOW_RECEEVED_REVIEWS') : t('ITEM.HIDE_RECEIVED_REVIEWS')}
                                            >
                                                <Icon icon={ChatBubbleLeftRightIcon} size="lg" variant="dark" className='tw-object-contain !tw-mt-[-2px]'/>
                                            </DialogTabsTrigger>
                                        }

                                        {
                                            (isSavedItem && persistedItem?.published__) &&
                                            <DialogTabsTrigger value="item-publication-status" onClick={()=> OnCloseItemReview()} title={t('ITEM.UPLOAD')}
                                                className='tw-flex tw-items-center tw-justify-center tw-w-[56px] tw-h-[56px]'>
                                                <Icon icon={ArrowUpTrayIcon} size="lg" variant="dark" className='tw-object-contain !tw-mt-[-2px]'/>
                                            </DialogTabsTrigger>
                                        }

                                        {
                                            isSavedItem &&
                                            <DialogTabsTrigger value="item-history" onClick={()=> OnCloseItemReview()} id="editor-history-tab" title={t('ITEM.HISTORY')}
                                                className='tw-flex tw-items-center tw-justify-center tw-w-[56px] tw-h-[56px]'>
                                                <Icon icon={Clockcounterwise} size='lg' variant="dark" className='tw-object-contain tw-mb-[0.5px] tw-aspect-[1/1]'/>
                                            </DialogTabsTrigger>
                                        }

                                        {
                                            isSavedItem &&
                                            <DialogTabsTrigger value="item-grouping" onClick={()=> OnCloseItemReview()} title={t('ITEM.SQUQRE_STACK')}
                                                className='tw-flex tw-items-center tw-justify-center tw-w-[56px] tw-h-[56px]'>
                                                <Icon icon={Square3Stack3DIcon} size="lg" variant="dark" className='tw-object-contain !tw-mt-[-2px]'/>
                                            </DialogTabsTrigger>
                                        }

                                        {
                                            isSavedItem &&
                                            <DialogTabsTrigger value="item-details" onClick={()=> OnCloseItemReview()} title={t('ITEM.CATALOG_VIEW')}
                                                className='tw-flex tw-items-center tw-justify-center tw-w-[56px] tw-h-[56px]'>
                                                <Icon icon={BookOpenIcon} size="lg" variant="dark" className='tw-object-contain !tw-mt-[-2px]'/>
                                            </DialogTabsTrigger>
                                        }

                                        { isSavedItem && persistedItem && <ItemHierarchiesTabTriggers item={persistedItem} /> }
                                    </DialogTabsList>

                                    <TabsContent
                                        value="item-editor"
                                        tabIndex={-1}
                                        className={`tw-flex-1 tw-min-h-0 tw-mt-0 ${editorTabPanelClassName}`}
                                    >
                                        <ItemEditorTab
                                            initialCategory={category}
                                            editorState={editorState}
                                            forceCloseTask={forceCloseTask}
                                            originalPrimaryKey={originalPrimaryKey__}
                                            persistedItem={persistedItem}
                                            itemOrigin={itemOrigin}
                                            templateForPreparedDefaultItem={templateForPreparedDefaultItem}
                                            task={task}
                                            openSections={openEditorSections}
                                            setOpenSections={setOpenEditorSections}
                                            scrollStickySection={scrollStickySection}
                                            onStickySectionScrolled={handleStickySectionScrolled}
                                        />
                                    </TabsContent>

                                    {
                                        isSavedItem && persistedItem &&
                                        <TabsContent
                                            value="item-history"
                                            tabIndex={-1}
                                            className={`data-[state=active]:tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-mt-0 ${editorTabPanelClassName}`}
                                        >
                                            <ItemHistoryTab primaryKey={persistedItem.primaryKey__} />
                                        </TabsContent>
                                    }
                                    {
                                        (isSavedItem && persistedItem?.published__) &&
                                        <TabsContent
                                            value="item-publication-status"
                                            tabIndex={-1}
                                            className={`data-[state=active]:tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-mt-0 ${editorTabPanelClassName}`}
                                        >
                                            <ItemPublicationsStatus primaryKey={selectedPrimaryKey} />
                                        </TabsContent>
                                    }
                                    {
                                        isSavedItem && persistedItem &&
                                        <TabsContent
                                            value="item-details"
                                            tabIndex={-1}
                                            className={`data-[state=active]:tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-mt-0 ${editorTabPanelClassName}`}
                                        >
                                            <ItemDetailsTab item={persistedItem} />
                                        </TabsContent>
                                    }

                                    {
                                        isSavedItem && persistedItem &&
                                        <TabsContent
                                            value="item-grouping"
                                            tabIndex={-1}
                                            className={`tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-mt-0 ${editorTabPanelClassName}`}
                                        >
                                            <ItemGroupingTab item={persistedItem} />
                                        </TabsContent>
                                    }

                                    {
                                        isSavedItem && persistedItem && <ItemHierarchiesTabs item={persistedItem} />
                                    }
                                </Tabs>
                            </>
                        )}

                    { onShowItemReview && <ItemReviewTab
                        primaryKey={selectedPrimaryKey}
                        item={persistedItem}
                        onReviewChange={handleReviewSectionChange}
                        onToggleReview={() => {
                            setOnShowItemReview(false);
                            setReviewSectionDirty(false);
                        }}
                    />}
                    { showReceivedReviews && <ItemReviews primaryKey={selectedPrimaryKey}/>}
                    <DialogFooter>
                        <div className='tw-flex tw-justify-center tw-text-center tw-py-2'>
                            {!(itemOrigin === ItemOrigin.NEW) &&( !isEmpty(items) || !isEmpty(sessionStorage.getItem('items'))) &&
                            <ItemsPaginationFooter
                                style={'tw-flex tw-flex-row tw-justify-center'}
                                isItemEditorDialog={true}
                                currentItemIndex={currentItemIndex}
                                onlineCatalogState={onlineItemsState}
                                hasUnsavedChanges={hasUnsavedChanges}
                                onNavigateToItem={(targetItem, index) => {
                                    unsetFocusAttribute();
                                    onNavigateToItem(targetItem.primaryKey__);
                                    setSelectedPrimaryKey(targetItem.primaryKey__);
                                    setCurrentItemIndex(index);
                                    setOnShowItemReview(false);
                                    setShowReceivedReviews(false);
                                    setReviewSectionDirty(false);
                                }}
                            />
                            }
                        </div>
                        {isLoadingPersistedItem ? null : <ConnectedUsers primaryKey={selectedPrimaryKey} />}
                        {!isLoadingPersistedItem && <EditorDialogButtons
                            closeItemEditor={closeItemEditor}
                            currentTab={resolvedTab}
                            isCreatingNewItem={isEmpty(savedItemPrimaryKey || selectedPrimaryKey)}
                            createItemOrigin={itemOrigin}
                            hasPreparedDefaultTemplate={!isEmpty(templateForPreparedDefaultItem)}
                            setShowConfirmationModal={ setShowConfirmationModal }
                            onItemSaved={handleItemSaved}
                            persistedItem={persistedItem}
                        />}
                    </DialogFooter>
                    <DiscardChangesConfirmationModal
                        title={t('MODAL.DISCARD_ITEM_REVIEW') + '?'}
                        onDiscard={handleReviewConfirmationDiscard}
                        onClose={handleReviewConfirmationClose}
                        show={showReviewConfirmationModal}
                    />
                    <ReloadItemDialog
                        message={reloadItemMessage}
                        open={openReloadEditorDialog}
                        handleReloadItemDialogClose={handleConfirmReload}
                    />
                </LayoutProvider>
            </DialogContent>
        </Dialog>
    );
};

// Main component that wraps everything with FormResetProvider and ItemEditorForm
const ItemEditorDialog = (props: ItemEditorDialogProps) => {
    return (
        <FormResetProvider>
            <ItemEditorForm>
                <ItemEditorDialogContent {...props} />
            </ItemEditorForm>
        </FormResetProvider>
    );
};

ItemEditorDialog.displayName = 'ItemEditorDialog';

export { ItemEditorDialog, type ItemEditorDialogProps };
